import { useCallback, useEffect, useState } from "react";
import { getNotifications, setNotifications, testNotifications, type QaNotifications } from "../../api/qaOrchestrator";
import { useAction, type SetError } from "./qaShared";

// The orchestrator's own check (notifications.ts DISCORD_WEBHOOK), so a typo shows before saving.
const WEBHOOK = /^https:\/\/(discord\.com|discordapp\.com|ptb\.discord\.com|canary\.discord\.com)\/api\/webhooks\/\d+\/[\w-]+$/;

/** Where the orchestrator posts "needs you" messages (GET/PUT /settings/notifications). The webhook URL is write-only. */
export default function QaNotificationsSection({ setError }: { setError: SetError }) {
  const [discord, setDiscord] = useState<QaNotifications["discord"] | null>(null);
  const [form, setForm] = useState({ url: "", mention: "" });
  const [sent, setSent] = useState(false);

  const apply = useCallback((d: QaNotifications["discord"]) => {
    setDiscord(d);
    setForm({ url: "", mention: d.mention ?? "" });
  }, []);
  const load = useCallback(async () => apply(await getNotifications()), [apply]);
  const { busy, run } = useAction(load, setError);
  useEffect(() => { load().catch((e) => setError((e as Error).message)); }, [load, setError]);

  if (!discord) return <section className="qo-section"><div className="members-empty">Loading…</div></section>;

  const url = form.url.trim();
  const urlOk = !url || WEBHOOK.test(url);
  const patch = {
    ...(url ? { discordWebhookUrl: url } : {}),
    ...((form.mention.trim() || null) !== discord.mention ? { mention: form.mention.trim() || null } : {}),
  };
  const changed = Object.keys(patch).length > 0;
  const save = (p: Parameters<typeof setNotifications>[0]) => run(async () => {
    setSent(false);
    await setNotifications(p);
  });
  const sendTest = () => run(async () => {
    setSent(false);
    await testNotifications();
    setSent(true);
  });

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Notifications</h3>
          <p>
            The orchestrator posts to a Discord channel when a computer needs a person (signed out of Minecraft, a window over the game, not answering)
            and when a test run is interrupted. Create the webhook in Discord under the channel's settings, Integrations, Webhooks.
          </p>
        </div>
      </div>
      <div className="qo-card qo-form">
        <div className="qo-grid">
          <label>
            Discord webhook URL
            <input aria-label="Discord webhook URL" type="password" autoComplete="new-password" placeholder={discord.configured ? "Enter a new URL to replace it" : "https://discord.com/api/webhooks/…"} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <small className={urlOk ? undefined : "qo-error-text"}>{urlOk ? `Webhook: ${discord.configured ? "set" : "not set"}` : "a Discord webhook URL: https://discord.com/api/webhooks/<id>/<token>"}</small>
          </label>
          <label>
            Mention
            <input aria-label="Mention" placeholder="<@&roleId>" value={form.mention} onChange={(e) => setForm({ ...form, mention: e.target.value })} />
            <small>Optional. Put in front of messages where someone has to act, e.g. {"<@&123…>"} for a role or {"<@123…>"} for a person.</small>
          </label>
        </div>
        <div className="qo-actions">
          <button className="btn btn-accent" disabled={busy || !changed || !urlOk} onClick={() => void save(patch)}>Save</button>
          <button className="btn btn-ghost" disabled={busy || !discord.configured} onClick={() => void sendTest()}>Send test message</button>
          <button className="btn btn-ghost" disabled={busy || !discord.configured} onClick={() => void save({ discordWebhookUrl: null })}>Clear webhook</button>
          {sent && <span role="status"><span className="qo-dot qo-dot-ok" />Sent: check the channel.</span>}
        </div>
        <p className="qo-hint">The URL is kept encrypted on the orchestrator and never shown again: anyone with it can post to the channel.</p>
      </div>
    </section>
  );
}
